'use client'

import { de } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { CaptionProps, DayPicker, DayPickerProps, useNavigation } from 'react-day-picker'

import { buttonVariants } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

export type CalendarProps = DayPickerProps

function CustomCaption({ calendarMonth }: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation()
  const currentYear = calendarMonth.date.getFullYear()
  const currentMonthIndex = calendarMonth.date.getMonth()

  const months = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ]

  // Generate year range (current year ± 10 years)
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i)

  const handleMonthChange = (month: string) => {
    const newDate = new Date(currentYear, parseInt(month), 1)
    goToMonth(newDate)
  }

  const handleYearChange = (year: string) => {
    const newDate = new Date(parseInt(year), currentMonthIndex, 1)
    goToMonth(newDate)
  }

  const handlePrevious = () => {
    if (previousMonth) goToMonth(previousMonth)
  }

  const handleNext = () => {
    if (nextMonth) goToMonth(nextMonth)
  }

  return (
    <div className="relative mb-2 flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={handlePrevious}
        disabled={!previousMonth}
        className={cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2">
        <Select value={currentMonthIndex.toString()} onValueChange={handleMonthChange}>
          <SelectTrigger className="h-8 w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((month, index) => (
              <SelectItem key={index} value={index.toString()}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={currentYear.toString()} onValueChange={handleYearChange}>
          <SelectTrigger className="h-8 w-[80px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="button"
        onClick={handleNext}
        disabled={!nextMonth}
        className={cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      defaultMonth={props.mode === 'single' ? props.selected : undefined}
      showOutsideDays={showOutsideDays}
      locale={de}
      className={cn('rounded-md bg-white p-3', className)}
      components={{
        MonthCaption: CustomCaption,
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'hidden',
        nav: 'hidden', // Hide default navigation since we handle it in CustomCaption
        nav_button: 'hidden',
        nav_button_previous: 'hidden',
        nav_button_next: 'hidden',
        table: 'w-full border-collapse space-y-1',
        week: 'flex items-center gap-1 py-[0.1rem]',
        weekdays: 'flex gap-1',
        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
        week_number_header: 'text-muted-foreground w-5 font-normal text-[0.8rem] text-center',
        head_row: 'flex gap-1',
        head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
        row: 'flex w-full mt-2 gap-1',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-[#27c226]/10 [&:has([aria-selected])]:bg-[#27c226]/10 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        weeknumber:
          'text-muted-foreground w-9 h-9 font-normal text-[0.8rem] flex items-center justify-center',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-full cursor-pointer'
        ),
        day_button: 'w-full h-full rounded-full',
        range_end: 'day-range-end',
        selected:
          'bg-[#27c226] text-white hover:bg-[#27c226] hover:text-white focus:bg-[#27c226] focus:text-white aria-selected:bg-[#27c226] aria-selected:hover:bg-[#27c226]',
        disabled: 'text-muted-foreground opacity-50',
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground hover:aria-selected:text-white',
        hidden: 'invisible',
        ...classNames,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
